import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SHORTFALL_REASONS,
  cents,
  formatRiel,
  formatUsd,
  type DeliveredLine,
  type ShortfallReason,
} from "@moomoo/core";
import { ApiError, api, type AdvanceInput, type OfficeOrder } from "../lib/api.js";
import { errorText, useLang, type Translate } from "../lib/i18n.js";
import { Counter, LockIcon, MapIcon, PhoneIcon } from "../ui/bits.js";

/**
 * The office and the driver. One PIN, one list, two taps per order.
 *
 * Nothing here moves stock: the truck is not a warehouse. The page records
 * where an order is and what was handed over, and the server does the rest.
 */

type Scope = "today" | "open" | "all";
type Payment = "cash" | "khqr" | "unpaid";

const REASON_KEY: Record<ShortfallReason, "reasonBroken" | "reasonReturn" | "reasonExpiry" | "reasonRefused" | "reasonOther"> = {
  ខូច: "reasonBroken",
  ត្រឡប់: "reasonReturn",
  ជិតផុតកំណត់: "reasonExpiry",
  មិនយក: "reasonRefused",
  ផ្សេងៗ: "reasonOther",
};

function orderIdFromHash(): string | undefined {
  const m = /^#([0-9a-f-]{36})$/.exec(window.location.hash);
  return m?.[1];
}

function PinPad({ t, onOpen }: { t: Translate; onOpen: () => void }) {
  const [pin, setPin] = useState("");
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setProblem(undefined);
    try {
      await api.office.login(pin.trim());
      onOpen();
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.code === "office_disabled") setProblem(t("officeOff"));
        else if (caught.code === "locked") setProblem(t("officeLocked"));
        else if (caught.code === "bad_pin") setProblem(t("officeBadPin"));
        else setProblem(errorText(t, caught));
      } else {
        setProblem(t("errGeneric"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pinpad">
      <div className="disc">
        <LockIcon />
      </div>
      <h2>{t("officeTitle")}</h2>
      <label htmlFor="pin">{t("officePin")}</label>
      <input
        id="pin"
        type="tel"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && pin.length >= 4) void submit();
        }}
      />
      {problem ? <div className="field-error">{problem}</div> : null}
      <button type="button" className="btn" onClick={() => void submit()} disabled={busy || pin.length < 4}>
        {t("officeEnter")}
      </button>
    </div>
  );
}

function DeliverySheet({
  order,
  t,
  lang,
  busy,
  onConfirm,
  onClose,
}: {
  order: OfficeOrder;
  t: Translate;
  lang: "en" | "kh";
  busy: boolean;
  onConfirm: (input: AdvanceInput) => void;
  onClose: () => void;
}) {
  const [qty, setQty] = useState<Record<string, number>>(
    Object.fromEntries(order.lines.map((l) => [l.code, l.qty])),
  );
  const [reason, setReason] = useState<Record<string, ShortfallReason | undefined>>({});
  const [payment, setPayment] = useState<Payment>(order.channel === "wholesale" ? "unpaid" : "cash");

  const shortLines = order.lines.filter((l) => (qty[l.code] ?? 0) < l.qty);
  const ready = shortLines.every((l) => reason[l.code]);

  function confirm() {
    const delivered: DeliveredLine[] = order.lines.map((l) => {
      const q = qty[l.code] ?? 0;
      const r = q < l.qty ? reason[l.code] : undefined;
      return { code: l.code, qty: q, ...(r ? { reason: r } : {}) };
    });
    onConfirm({ stage: "delivered", delivered, payment });
  }

  return (
    <div className="sheet">
      <div className="kv" style={{ padding: 0 }}>
        <span>{t("deliveredWhat")}</span>
      </div>
      {order.lines.map((l) => {
        const q = qty[l.code] ?? 0;
        const short = q < l.qty;
        return (
          <div key={l.code}>
            <div className="dl">
              <span className="nm">
                {lang === "kh" ? l.nameKh : l.nameEn}
                <small>
                  {t("ordered")} {l.qty}
                </small>
              </span>
              <Counter
                qty={q}
                label={l.nameEn}
                onDown={() => setQty((s) => ({ ...s, [l.code]: Math.max(0, q - 1) }))}
                onUp={() => setQty((s) => ({ ...s, [l.code]: Math.min(l.qty, q + 1) }))}
              />
            </div>
            {short ? (
              <div className="chips" style={{ padding: "0 0 6px" }}>
                {SHORTFALL_REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={reason[l.code] === r}
                    onClick={() => setReason((s) => ({ ...s, [l.code]: r }))}
                  >
                    {lang === "kh" ? r : t(REASON_KEY[r])}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}

      {order.channel === "wholesale" ? (
        <p className="note">{t("invoice")}</p>
      ) : (
        <>
          <div className="kv" style={{ padding: "8px 0 0" }}>
            <span>{t("paymentAtDoor")}</span>
          </div>
          <div className="chips" style={{ padding: "6px 0 0" }}>
            {(
              [
                ["cash", "payCash"],
                ["khqr", "payKhqrDone"],
                ["unpaid", "payNotYet"],
              ] as const
            ).map(([value, key]) => (
              <button key={value} type="button" aria-pressed={payment === value} onClick={() => setPayment(value)}>
                {t(key)}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="picker-actions">
        <button type="button" className="btn quiet" onClick={onClose} disabled={busy}>
          {t("cancel")}
        </button>
        <button type="button" className="btn done" onClick={confirm} disabled={busy || !ready}>
          {t("confirmDelivery")}
        </button>
      </div>
    </div>
  );
}

function Card({
  order,
  t,
  lang,
  hit,
  onAdvance,
}: {
  order: OfficeOrder;
  t: Translate;
  lang: "en" | "kh";
  hit: boolean;
  onAdvance: (id: string, input: AdvanceInput) => Promise<void>;
}) {
  const [sheet, setSheet] = useState<"deliver" | "cancel" | undefined>();
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();

  async function go(input: AdvanceInput) {
    setBusy(true);
    setProblem(undefined);
    try {
      await onAdvance(order.id, input);
      setSheet(undefined);
    } catch (caught) {
      setProblem(caught instanceof ApiError ? errorText(t, caught) : t("errGeneric"));
    } finally {
      setBusy(false);
    }
  }

  const stageKey = {
    placed: "stagePlaced",
    confirmed: "stageConfirmed",
    packed: "stageConfirmed",
    on_the_way: "stageOnTheWay",
    delivered: "stageDelivered",
    cancelled: "stageCancelled",
  } as const;
  const fresh = order.stage === "placed";
  const open = order.stage === "confirmed" || order.stage === "packed";
  const finished = order.stage === "delivered" || order.stage === "cancelled";
  const delivered = new Map((order.delivered ?? []).map((d) => [d.code, d]));
  const paid = order.payment?.status === "paid";
  const mapUrl = order.location ? `https://maps.google.com/?q=${order.location.lat},${order.location.lng}` : undefined;

  return (
    <div className={`ocard${hit ? " hit" : ""}${finished ? " done" : ""}`} id={`o-${order.id}`}>
      <div className="who">
        <b>{order.customer?.name ?? order.erpName ?? order.id.slice(0, 8)}</b>
        <span className={`stage ${order.stage}`}>{fresh ? t("officeNew") : t(stageKey[order.stage])}</span>
      </div>
      <div className="addr">
        {order.customer?.address}
        {order.customer?.note ? (
          <>
            <br />
            {t("noteLabel")}: {order.customer.note}
          </>
        ) : null}
      </div>
      <div className="meta">
        {order.km} km · {order.erpName ?? order.id.slice(0, 8)} · {order.channel === "wholesale" ? t("wholesale") : t("retail")}
      </div>
      <div className="links">
        {order.phone ? (
          <a href={`tel:${order.phone}`}>
            <PhoneIcon />
            {order.phone}
          </a>
        ) : null}
        {mapUrl ? (
          <a href={mapUrl} target="_blank" rel="noreferrer">
            <MapIcon />
            {t("map")}
          </a>
        ) : null}
      </div>

      <div className="lines">
        {order.lines.map((l) => {
          const d = delivered.get(l.code);
          const short = d && d.qty < l.qty;
          return (
            <div key={l.code} className={short ? "short" : ""}>
              <span>
                {lang === "kh" ? l.nameKh : l.nameEn} × {l.qty}
                {short ? ` → ${d.qty}${d.reason ? ` (${d.reason})` : ""}` : ""}
              </span>
              <span>{formatUsd(cents(l.priceCents * (d ? d.qty : l.qty)))}</span>
            </div>
          );
        })}
      </div>
      <div className="sum">
        <span>
          {order.channel === "wholesale" ? t("invoice") : paid ? t("paid") : t("cashOnDelivery")}
        </span>
        <span>
          {formatUsd(cents(order.totalCents))} · {formatRiel(cents(order.totalCents))}
        </span>
      </div>

      {problem ? <div className="field-error">{problem}</div> : null}

      {sheet === "deliver" ? (
        <DeliverySheet order={order} t={t} lang={lang} busy={busy} onConfirm={(i) => void go(i)} onClose={() => setSheet(undefined)} />
      ) : sheet === "cancel" ? (
        <div className="sheet">
          <label htmlFor={`c-${order.id}`}>{t("cancelWhy")}</label>
          <input id={`c-${order.id}`} type="text" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          <div className="picker-actions">
            <button type="button" className="btn quiet" onClick={() => setSheet(undefined)} disabled={busy}>
              {t("cancel")}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || !cancelReason.trim()}
              onClick={() => void go({ stage: "cancelled", reason: cancelReason.trim() })}
            >
              {t("cancelOrder")}
            </button>
          </div>
        </div>
      ) : fresh ? (
        <>
          <button type="button" className="btn accept" disabled={busy} onClick={() => void go({ stage: "confirmed" })}>
            {t("officeAccept")}
          </button>
          <button type="button" className="btn danger" onClick={() => setSheet("cancel")}>
            {t("cancelOrder")}
          </button>
        </>
      ) : open ? (
        <>
          <button type="button" className="btn out" disabled={busy} onClick={() => void go({ stage: "on_the_way" })}>
            {t("takeIt")}
          </button>
          <button type="button" className="btn danger" onClick={() => setSheet("cancel")}>
            {t("cancelOrder")}
          </button>
        </>
      ) : order.stage === "on_the_way" ? (
        <>
          <button type="button" className="btn done" disabled={busy} onClick={() => setSheet("deliver")}>
            {t("handedOver")}
          </button>
          <button type="button" className="btn danger" onClick={() => setSheet("cancel")}>
            {t("cancelOrder")}
          </button>
        </>
      ) : null}
    </div>
  );
}

export function Office() {
  const { lang, t, toggle } = useLang();
  const [signedIn, setSignedIn] = useState<boolean | undefined>();
  // "open" first: after the 09:00 cutoff a new order is for tomorrow and would hide behind "today"
  const [scope, setScope] = useState<Scope>("open");
  const [orders, setOrders] = useState<readonly OfficeOrder[]>([]);
  const [problem, setProblem] = useState<string | undefined>();
  const hit = useRef(orderIdFromHash());

  const load = useCallback(async () => {
    try {
      setOrders(await api.office.orders(scope));
      setSignedIn(true);
      setProblem(undefined);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 401 || caught.status === 503)) {
        setSignedIn(false);
        return;
      }
      setProblem(caught instanceof ApiError ? errorText(t, caught) : t("errGeneric"));
    }
  }, [scope, t]);

  useEffect(() => {
    void load();
    // no polling while signed out: a restarted server would otherwise be hit every 15 s
    if (signedIn === false) return;
    const timer = setInterval(() => void load(), 15_000);
    return () => clearInterval(timer);
  }, [load, signedIn]);

  useEffect(() => {
    if (!hit.current || orders.length === 0) return;
    document.getElementById(`o-${hit.current}`)?.scrollIntoView({ block: "center" });
  }, [orders]);

  const advance = useCallback(
    async (id: string, input: AdvanceInput) => {
      const updated = await api.office.advance(id, input);
      setOrders((list) => list.map((o) => (o.id === id ? updated : o)));
    },
    [],
  );

  const counts = useMemo(() => {
    const fresh = orders.filter((o) => o.stage === "placed").length;
    const toDeliver = orders.filter((o) => o.stage === "confirmed" || o.stage === "packed").length;
    const onWay = orders.filter((o) => o.stage === "on_the_way").length;
    const delivered = orders.filter((o) => o.stage === "delivered").length;
    const cash = orders
      .filter((o) => o.stage === "delivered" && o.payment?.method === "cash" && o.payment.status === "paid")
      .reduce((sum, o) => sum + o.totalCents, 0);
    return { fresh, toDeliver, onWay, cash };
  }, [orders]);

  const today = new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 10);

  return (
    <div className="shell">
      <div className="office-head">
        <h1>
          <span>
            {t("brand")} · {t("officeTitle")}
          </span>
          <span>
            <a href="/office/install" className="headlink">
              {t("installTitle")}
            </a>
            <button type="button" onClick={toggle}>
              {lang === "en" ? "ភាសាខ្មែរ" : "English"}
            </button>
          </span>
        </h1>
        <div className="date">{today}</div>
        {signedIn ? (
          <div className="counts">
            <div className={counts.fresh > 0 ? "hot" : ""}>
              <b>{counts.fresh}</b>
              <span>{t("officeNew")}</span>
            </div>
            <div className="todo">
              <b>{counts.toDeliver}</b>
              <span>{t("countToDeliver")}</span>
            </div>
            <div className="way">
              <b>{counts.onWay}</b>
              <span>{t("countOnWay")}</span>
            </div>
            <div className="cash">
              <b>{formatUsd(cents(counts.cash))}</b>
              <span>{t("countCash")}</span>
            </div>
          </div>
        ) : null}
      </div>

      {signedIn === false ? (
        <PinPad t={t} onOpen={() => void load()} />
      ) : signedIn === undefined ? (
        <div className="pad">
          <p className="note">{t("loading")}</p>
        </div>
      ) : (
        <>
          <div className="chips" role="tablist">
            {(
              [
                ["open", "tabOpen"],
                ["today", "tabToday"],
                ["all", "tabAll"],
              ] as const
            ).map(([value, key]) => (
              <button key={value} type="button" role="tab" aria-pressed={scope === value} onClick={() => setScope(value)}>
                {t(key)}
              </button>
            ))}
          </div>
          {problem ? <div className="banner bad">{problem}</div> : null}
          {orders.length === 0 ? (
            <div className="empty">
              <b>{t("noOrders")}</b>
            </div>
          ) : (
            <div className="ocards">
              {orders.map((o) => (
                <Card key={o.id} order={o} t={t} lang={lang} hit={o.id === hit.current} onAdvance={advance} />
              ))}
            </div>
          )}
          <div style={{ height: 40 }} />
        </>
      )}
    </div>
  );
}
